import { useCallback, useEffect, useRef, useState } from 'react';
import {
	SelfieFdEngine,
	OVERLAY_SIZE,
	REQUIRED_STABLE_FRAMES,
} from '../lib/selfie-fd/selfie-fd-engine';
import type { LivenessSnapshot } from '../lib/selfie-fd/selfie-fd.model';

const idleHint = (hint: string): LivenessSnapshot => ({
	faceDetected: false,
	centered: false,
	largeEnough: false,
	movementDetected: false,
	stableFrames: 0,
	canCapture: false,
	primaryHint: hint,
	guideTone: 'idle',
});

export function useSelfieFd() {
	const engineRef = useRef<SelfieFdEngine | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const overlayRef = useRef<HTMLCanvasElement | null>(null);

	const [isLoading, setIsLoading] = useState(true);
	const [isLoaded, setIsLoaded] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isStreaming, setIsStreaming] = useState(false);
	const [snapshot, setSnapshot] = useState<LivenessSnapshot>(
		idleHint('Loading face detection…'),
	);

	if (!engineRef.current) {
		engineRef.current = new SelfieFdEngine();
	}

	useEffect(() => {
		const engine = engineRef.current!;
		engine.onSnapshot(setSnapshot);

		let cancelled = false;
		setIsLoading(true);
		engine
			.preloadDetector()
			.then(() => {
				if (cancelled) return;
				setIsLoaded(true);
				setError(null);
				setSnapshot(idleHint('Position your face in the oval'));
			})
			.catch((err) => {
				if (cancelled) return;
				console.error('Failed to load MediaPipe face detector:', err);
				setError(
					'Failed to load face detection. Check your internet connection and try again.',
				);
				setIsLoaded(false);
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});

		return () => {
			cancelled = true;
			engine.onSnapshot(null);
			engine.detach();
		};
	}, []);

	const attachElements = useCallback(() => {
		const engine = engineRef.current;
		const video = videoRef.current;
		const overlay = overlayRef.current;
		if (!engine || !video || !overlay) return false;

		overlay.width = OVERLAY_SIZE;
		overlay.height = OVERLAY_SIZE;
		if (!engine.isAttached) {
			engine.attach(video, overlay);
			engine.onSnapshot(setSnapshot);
		}
		return true;
	}, []);

	const startCamera = useCallback(
		async (facingMode: 'user' | 'environment' = 'user') => {
			const engine = engineRef.current;
			if (!engine) return;

			if (!attachElements()) {
				throw new Error('Camera elements are not ready');
			}

			engine.setFacingMode(facingMode);
			setIsStreaming(true);
			try {
				await engine.startCamera();
			} catch (err) {
				setIsStreaming(false);
				throw err;
			}
		},
		[attachElements],
	);

	const stopCamera = useCallback(async () => {
		const engine = engineRef.current;
		if (!engine) return;
		await engine.stopCamera();
		setIsStreaming(false);
		setSnapshot(idleHint('Position your face in the oval'));
	}, []);

	const captureSelfie = useCallback(async (): Promise<File> => {
		const engine = engineRef.current;
		if (!engine) {
			throw new Error('Face engine not ready');
		}
		const blob = await engine.captureSelfie();
		return new File([blob], 'selfie.png', { type: 'image/png' });
	}, []);

	return {
		videoRef,
		overlayRef,
		isLoading,
		isLoaded,
		error,
		isStreaming,
		snapshot,
		canCapture: snapshot.canCapture,
		requiredStableFrames: REQUIRED_STABLE_FRAMES,
		startCamera,
		stopCamera,
		captureSelfie,
		attachElements,
	};
}
